import React from "react";
import { Briefcase } from "lucide-react";

interface WorkingHoursToggleProps {
  active: boolean;
  onClick: () => void;
}

/**
 * Switch button for the "Working hours only" flag. Used by both the task
 * panel and the subtask panel in TaskDialog — extracted so the two stay in
 * lockstep on icon, copy, animation, and styling.
 *
 * Validation (blocking the toggle when the current slot is out of window)
 * lives in the caller, since the parent owns the blocking-dialog state.
 */
const WorkingHoursToggle: React.FC<WorkingHoursToggleProps> = ({
  active,
  onClick,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-soft text-sm transition-all duration-150 ease-in-out text-left ${
      active
        ? "bg-warm-blue/10 border border-warm-blue/30"
        : "bg-warm-bg-elevated border border-warm-border hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
    }`}
  >
    <Briefcase
      size={16}
      className={
        active
          ? "text-warm-blue flex-shrink-0"
          : "text-warm-text-tertiary flex-shrink-0"
      }
    />
    <div className="flex-1 min-w-0">
      <div
        className={
          active ? "font-medium text-warm-blue" : "text-warm-text-secondary"
        }
      >
        Working hours only
      </div>
      <div className="text-[11px] text-warm-text-tertiary leading-tight mt-0.5">
        Mon–Fri, 9 AM – 5 PM
      </div>
    </div>
    <div
      className={`relative w-9 h-5 rounded-full transition-colors duration-150 ease-in-out flex-shrink-0 ${
        active ? "bg-warm-blue" : "bg-warm-border"
      }`}
    >
      <div
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-150 ease-in-out ${
          active ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </div>
  </button>
);

export default WorkingHoursToggle;
